import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { PointsProvider } from './hooks/PointsContext';
import { AudioProvider } from './hooks/AudioContext';
import { PermissionsProvider } from './hooks/PermissionsContext';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from './auth/authConfig';

// storeAuthStateInCookie:true causes MSAL to write the interaction lock to BOTH
// sessionStorage AND first-party cookies. iOS Safari persists session cookies across
// app restarts, so a previous abandoned login leaves a stale msal.interaction.status
// cookie that blocks all future loginRedirect calls. Clear both stores on every load
// unless we are actually handling a redirect response right now.
const clearMsalInteractionLock = () => {
  // sessionStorage
  Object.keys(sessionStorage)
    .filter(k => k.includes('interaction.status'))
    .forEach(k => { console.log('[MSAL] clearing stale session lock:', k); sessionStorage.removeItem(k); });
  // cookies (written by storeAuthStateInCookie:true)
  document.cookie.split(';').forEach(c => {
    const name = c.trim().split('=')[0];
    if (name && name.includes('interaction.status')) {
      console.log('[MSAL] clearing stale cookie lock:', name);
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  });
};

const _API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';

msalInstance.initialize()
  .then(() => msalInstance.handleRedirectPromise().catch(() => null))
  .then((redirectResult) => {
    const hasRedirectResponse = window.location.hash.includes('code=') ||
      window.location.search.includes('code=');
    if (!hasRedirectResponse) {
      clearMsalInteractionLock();
    }

    // Fire login event when MSAL resolves a successful redirect (account returned)
    if (redirectResult && redirectResult.account) {
      const acct = redirectResult.account;
      // Store OID so view tracking can include it without requiring auth on every request
      try { sessionStorage.setItem('vusub_account_id', acct.localAccountId || ''); } catch { /* ignore */ }
      fetch(`${_API_URL}/api/admin/login-events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: acct.username || '',
          user_oid: acct.localAccountId || '',
          outcome: 'success',
          user_agent: navigator.userAgent,
        }),
      }).catch(() => {}); // fire-and-forget
    }

    // For users who are already signed in (cached MSAL session, no fresh redirect),
    // ensure vusub_account_id is stored so view-tracking events include their account_id.
    if (!sessionStorage.getItem('vusub_account_id')) {
      const cachedAccounts = msalInstance.getAllAccounts();
      if (cachedAccounts.length > 0) {
        try { sessionStorage.setItem('vusub_account_id', cachedAccounts[0].localAccountId || ''); } catch { /* ignore */ }
      }
    }

    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <PermissionsProvider>
            <PointsProvider initialBalance={50}>
              <AudioProvider>
                <App />
              </AudioProvider>
            </PointsProvider>
          </PermissionsProvider>
        </BrowserRouter>
      </MsalProvider>
    </React.StrictMode>
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Register the no-op PWA service worker (HTTPS only).
// Enables Android Chrome's native "Add to Home Screen" install prompt.
// The SW has no fetch handler so it cannot affect video, auth, or API calls.
if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {
      // Silent — a failed SW registration must never break the app.
    });
  });
}
